import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import styles from "./PreviewCompletePage.module.scss";

import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import { FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type Checklist = {
  practice_details?: boolean;
  logo_uploaded?: boolean;
  social_links?: boolean;
  portofolio_image?: boolean;
  consent_templates?: boolean;
};

type PracticeDetails = {
  name?: string;
  address?: string;
  contact_no?: string;
  email?: string;
  logo?: string;
};


type SiteDetails = {
  name?: string;
  address?: string | null;
  contact_no?: string | null;
  email?: string | null; // your JSON shows email: null (links.email exists)
  logo?: string;
  links?: {
    email?: string;
    website?: string;
    whatsapp?: string;
    instagram?: string;
    facebook?: string;
    linkedin?: string;
    tiktok?: string;
    google_maps?: string;
    google_review?: string;
  };
};


export default function PreviewCompletePage() {
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [practice, setPractice] = useState<PracticeDetails | null>(null);
  const [site, setSite] = useState<SiteDetails | null>(null);
  const [documentHTML, setDocumentHTML] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch onboarding summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
       const res = await axios.get(
      `${process.env.REACT_APP_BASE_URL_V2}onboarding/onboarding_summary`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

        const data = res.data;
        setChecklist(data.checklist);
        setPractice(data.practice_details);
        setSite(data.site_details);
        setDocumentHTML(data.document_preview_html);
      } catch (err: any) {
        console.error("Failed to load onboarding summary", err);
        toast.error("Failed to load onboarding summary");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <Header />
        <main className={styles.main}>
          <p>Loading onboarding summary...</p>
        </main>
      </div>
    );
  }

  const completion = 100; // optional: can come from backend

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={6}
          rightLabel="100% Complete"
          onStepClick={(step) => navigate(step.path)}
        />


        <h1 className={styles.title}>Preview and complete</h1>
        <p className={styles.subtitle}>
          Here’s a preview of how your branding will appear in documents and marketing materials
        </p>

        {/* ---------- Document review section ---------- */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Document review</h2>

          {/* Practice + Doctor Info Header */}
          {/* <div className={styles.reviewHeader}>
            <div className={styles.personBlock}>
              <span className={styles.badge} aria-hidden>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M12 12a5 5 0 100-10 5 5 0 000 10zm8 9v-1a7 7 0 00-7-7H11a7 7 0 00-7 7v1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <div>
                <div className={styles.personName}>{practice?.name || "Practice Name"}</div>
                <div className={styles.personLine}>{practice?.address || "Practice Address"}</div>
                <div className={styles.personLine}>{practice?.contact_no || "Phone Number"}</div>
              </div>
            </div>

            <div className={styles.personBlockRight}>
              <span className={styles.badge} aria-hidden>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M12 12a5 5 0 100-10 5 5 0 000 10zm8 9v-1a7 7 0 00-7-7H11a7 7 0 00-7 7v1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <div>
                <div className={styles.personName}>Dr. Name</div>
                <div className={styles.personLine}>Professional title</div>
              </div>
            </div>
          </div> */}

          {/* Render HTML from backend */}
          {documentHTML ? (
            <div
              className={styles.previewBox}
              dangerouslySetInnerHTML={{ __html: documentHTML }}
            />
          ) : (
            <p className={styles.textPara}>
              No preview available. Please complete your consent templates.
            </p>
          )}

          
          {/* Footer Info */}
          <div className={styles.addressRow}>
            <div>
              <div className={styles.personNameSmall}>
                {site?.name || "Practice Name"}
              </div>
              <div className={styles.personLine}>
                {site?.address || "Practice Address"}
              </div>
            </div>

            <div className={styles.addrRight}>
              <div className={styles.personLine}>
                {site?.contact_no || "Phone"}
              </div>

              <div className={styles.personLine}>
                {site?.email || site?.links?.email || "Email"}
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Setup Summary ---------- */}
        <div className={styles.twoCol}>
          <section className={styles.smallCard}>
            <h3 className={styles.smallTitle}>Setup Summary</h3>
            <ul className={styles.summaryList}>
              <li>
                <span>Practice Information</span>
                {checklist?.practice_details ? (
                  <FiCheckCircle className={styles.ok} />
                ) : (
                  <em>Pending</em>
                )}
              </li>
              <li>
                <span>Logo Upload</span>
                {checklist?.logo_uploaded ? (
                  <FiCheckCircle className={styles.ok} />
                ) : (
                  <em>Optional</em>
                )}
              </li>
              <li>
                <span>Social Media Links</span>
                {checklist?.social_links ? (
                  <FiCheckCircle className={styles.ok} />
                ) : (
                  <em>Optional</em>
                )}
              </li>
              <li>
                <span>Portfolio Images</span>
                {checklist?.portofolio_image ? (
                  <FiCheckCircle className={styles.ok} />
                ) : (
                  <em>Optional</em>
                )}
              </li>
              <li>
                <span>Consent Templates</span>
                {checklist?.consent_templates ? (
                  <FiCheckCircle className={styles.ok} />
                ) : (
                  <em>Optional</em>
                )}
              </li>
            </ul>
          </section>

          {/* ---------- What's Next ---------- */}
          <section className={styles.smallCard}>
            <h3 className={styles.smallTitle}>What’s Next?</h3>
            <ul className={styles.nextList}>
              <li>Your branding toolkit is now ready! You can:</li>
              <li>Generate branded consent forms</li>
              <li>Create marketing materials</li>
              <li>Update your information anytime</li>
              <li>Add more consent templates</li>
            </ul>
          </section>
        </div>

        {/* ---------- Actions ---------- */}
        <div className={styles.actions}>
          <button className={styles.btnSecondary} onClick={() => navigate("/consentTemplates")}>
            Back
          </button>
          <button className={styles.btnPrimary} onClick={() => navigate("/complete")}>
            Complete setup
          </button>
        </div>

        <div className={styles.helpNote}>
          Need assistance with your branding toolkit? Contact our support team at{" "}
          <strong>support@medicube.com</strong>
        </div>
      </main>

      <footer className={styles.footerBar}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>
    </div>
  );
}
